import { useCallback, useMemo, useSyncExternalStore } from "react";

import type { BoostBillingAddress } from "@/api/boosts";

const STORAGE_KEY = "boost.pending";
const TTL_MS = 30 * 60_000;
// Same-tab change channel. The native `storage` event only fires across tabs
// for `localStorage`, so sessionStorage writes must broadcast their own event
// for live readers in the same tab to see updates.
const CHANGE_EVENT = "boost.pending:change";

type PendingBoost = {
  statusId: string;
  taxQuoteToken: string;
  /**
   * Absent when the gateway rejected the address: the entry still anchors the
   * already-published Truth, but the refused address must not be replayable.
   * The recovery flow re-collects one.
   */
  billingAddress?: BoostBillingAddress;
  createdAt: number;
  /** When the most recent attempt failed; populated by save callers that know it failed. */
  failedAt?: number;
  /** One-line reason for the failure, used by the dashboard banner (TMT-90). */
  reason?: string;
};

function safeSessionStorage(): Storage | null {
  try {
    if (typeof window === "undefined") return null;
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function emitChange() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

/**
 * Remove the pending-boost recovery entry. Call this from logout / account-
 * switch paths so the departing user's billing-address PII, tax quote token,
 * and status id don't survive into the next account on a shared device — the
 * sessionStorage entry persists across an in-app switch with no full reload
 * (e.g. `switchAccount({ shouldRedirect: false })`).
 *
 * Mirrors `clearStoredBoostBillingAddress`: lives next to the hook that owns
 * the key and is safe to call outside React (no hooks).
 */
function clearStoredPendingBoost(): void {
  safeSessionStorage()?.removeItem(STORAGE_KEY);
  emitChange();
}

function readPending(): PendingBoost | null {
  const storage = safeSessionStorage();
  if (!storage) return null;
  const raw = storage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as PendingBoost;
    if (!parsed || typeof parsed.createdAt !== "number") {
      storage.removeItem(STORAGE_KEY);
      return null;
    }
    if (Date.now() - parsed.createdAt > TTL_MS) {
      storage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    storage.removeItem(STORAGE_KEY);
    return null;
  }
}

/**
 * Persist a "post was created but boost failed" state so that a follow-up
 * surface (TMT-57 dashboard) can offer a one-tap retry within the 30-minute
 * tax quote window. Entries self-expire on read past TTL.
 */
function useBoostRecovery() {
  const save = useCallback((pending: Omit<PendingBoost, "createdAt">) => {
    const storage = safeSessionStorage();
    if (!storage) return;
    const payload: PendingBoost = { ...pending, createdAt: Date.now() };
    try {
      storage.setItem(STORAGE_KEY, JSON.stringify(payload));
      emitChange();
    } catch {
      // Quota exceeded or serialization failed — non-fatal.
    }
  }, []);

  const read = useCallback(() => readPending(), []);

  const clear = useCallback(() => {
    const storage = safeSessionStorage();
    storage?.removeItem(STORAGE_KEY);
    emitChange();
  }, []);

  // Memoize the returned object so consumers that pass `recovery` into a
  // useEffect dep array don't re-fire on every parent render.
  return useMemo(() => ({ save, read, clear }), [save, read, clear]);
}

function subscribePending(callback: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  // `storage` only fires cross-tab and only for `localStorage`, so gate the
  // native listener on the matching key to avoid re-rendering on unrelated
  // writes from other tabs (e.g. another feature's sessionStorage usage).
  // The `e.key === null` case is `storage.clear()` and must invalidate too.
  const storageHandler = (e: StorageEvent) => {
    if (e.key === STORAGE_KEY || e.key === null) callback();
  };
  window.addEventListener(CHANGE_EVENT, callback);
  window.addEventListener("storage", storageHandler);

  // TTL boundary: if a pending entry exists, schedule a one-shot wake to
  // re-snapshot when it expires so the banner clears without depending on
  // another event (the dashboard tab can sit idle for the full 30 minutes).
  // `setTimeout` is throttled on hidden tabs, but the throttle resolves to
  // firing late, never early — acceptable; a late clear is still correct.
  let expiryTimer: ReturnType<typeof setTimeout> | undefined;
  const current = readPending();
  if (current) {
    const remainingMs = current.createdAt + TTL_MS - Date.now();
    if (remainingMs > 0) {
      expiryTimer = setTimeout(() => {
        // Force the cached snapshot to re-evaluate by clearing the cache
        // key, then notify subscribers. Storage gets cleaned up by
        // readPending() on the next snapshot call.
        cachedRaw = null;
        callback();
      }, remainingMs);
    }
  }

  return () => {
    window.removeEventListener(CHANGE_EVENT, callback);
    window.removeEventListener("storage", storageHandler);
    if (expiryTimer !== undefined) clearTimeout(expiryTimer);
  };
}

// useSyncExternalStore requires a stable snapshot — returning a fresh object
// on each call would loop. Cache the JSON we last parsed; only allocate a new
// object when the underlying string changes OR when the cached snapshot has
// crossed its TTL boundary since last read.
let cachedRaw: string | null = null;
let cachedSnapshot: PendingBoost | null = null;
function getPendingSnapshot(): PendingBoost | null {
  const storage = safeSessionStorage();
  const raw = storage ? storage.getItem(STORAGE_KEY) : null;
  if (raw === cachedRaw && cachedSnapshot) {
    // Cache hit on the JSON string — but the cached snapshot may have aged
    // past TTL since the last read. Without this re-check, a banner that
    // mounted at T=29:59 would keep showing the stale entry past T=30:00
    // until something else mutated storage.
    if (Date.now() - cachedSnapshot.createdAt > TTL_MS) {
      storage?.removeItem(STORAGE_KEY);
      cachedRaw = null;
      cachedSnapshot = null;
      return null;
    }
    return cachedSnapshot;
  }
  cachedRaw = raw;
  cachedSnapshot = readPending();
  return cachedSnapshot;
}
function getPendingServerSnapshot(): PendingBoost | null {
  return null;
}

/** Test-only: reset the module-scoped snapshot cache between specs. */
function __resetPendingSnapshotCache(): void {
  cachedRaw = null;
  cachedSnapshot = null;
}

/**
 * Reactive read of the pending boost — re-renders when `save`/`clear` are
 * called in the same tab (via the CHANGE_EVENT) or when sessionStorage is
 * mutated cross-tab (via the native `storage` event).
 */
function usePendingBoost(): PendingBoost | null {
  return useSyncExternalStore(
    subscribePending,
    getPendingSnapshot,
    getPendingServerSnapshot,
  );
}

export {
  useBoostRecovery,
  usePendingBoost,
  clearStoredPendingBoost,
  __resetPendingSnapshotCache,
  type PendingBoost,
};
